import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { Loader } from '@scuf/common';
import { authService } from '../../auth/services';
import { getMCDEnabled } from '../../config/authConfig';
import i18next from '../../i18next';
import './Integration.scss';

export interface EasyIntegrateOrg {
  id?: string;
  name?: string;
  siteId?: string;
}

interface EasyIntegrateIframeProps {
  /** Relative path on the EasyIntegrate host, e.g. "/workflows" or "/templates" */
  path: string;
  /** Currently selected organisation / site context passed down to the iframe */
  orgSelected?: EasyIntegrateOrg | null;
  /** Optional override for the iframe element id (kept unique per mounted tab) */
  iframeId?: string;
  /**
   * Notifies the host page when a Node-RED editor is opened/closed so the page can
   * hide its header + tabs and let the editor fill the whole integration page.
   */
  onEditorStateChange?: (isEditorOpen: boolean) => void;
}

/** Imperative handle the host page can use to control the iframe (e.g. on browser back). */
export interface EasyIntegrateIframeHandle {
  /** Close any open Node-RED editor and restore the EasyIntegrate landing view. */
  closeEditor: () => void;
}

// SECURITY_NOTE: The EasyIntegrate host is externalized via env config (public/config/config.js).
// Never hardcode this value here — update EASY_INTEGRATE_BASE in config.js instead.
const EASY_INTEGRATE_BASE: string = (window as any).ENV?.EASY_INTEGRATE_BASE || '';

const selectedLoginOption = 'selectedLoginOption';

const getWindowInnerHeight = () => window.innerHeight;
// Vertical space reserved above the iframe.
//  - default view: portal header + integration title/customer + tabs
//  - editor view: header + tabs hidden, so the editor uses (almost) the full height
const RESERVE_DEFAULT = 220;
const RESERVE_EDITOR = 60;
const getContentHeight = (innerHeight: number, reserve: number) =>
  Math.max(innerHeight - reserve, 400);

const safeOrigin = (url: string): string => {
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
};

// SECURITY_NOTE: Node-RED editors are served from dynamic origins (e.g.
// https://nodered-operator-*.apps.*.honeywell.com or http://localhost:5002 in dev).
// We can't enumerate them up-front, so we validate by policy instead of a fixed list:
//  - the configured EasyIntegrate host origin
//  - any *.honeywell.com host (prod/dev clusters)
//  - localhost / 127.0.0.1 (local development only)
//  - any extra origins explicitly configured via ENV.EASY_INTEGRATE_TRUSTED_ORIGINS
// This keeps the allow-list tight while still accepting legitimate editor frames.
const configuredTrustedOrigins: string[] = (() => {
  const raw = (window as any).ENV?.EASY_INTEGRATE_TRUSTED_ORIGINS;
  if (Array.isArray(raw)) return raw.map(safeOrigin).filter(Boolean);
  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map(s => safeOrigin(s.trim()))
      .filter(Boolean);
  }
  return [];
})();

const isTrustedOrigin = (origin: string, staticAllowList: Set<string>): boolean => {
  if (!origin) return false;
  if (staticAllowList.has(origin)) return true;
  if (configuredTrustedOrigins.includes(origin)) return true;
  let hostname = '';
  try {
    hostname = new URL(origin).hostname;
  } catch {
    return false;
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
  if (hostname === 'honeywell.com' || hostname.endsWith('.honeywell.com')) {
    return true;
  }
  return false;
};

const EasyIntegrateIframe = forwardRef<
  EasyIntegrateIframeHandle,
  EasyIntegrateIframeProps
>(
  (
    {
      path,
      orgSelected,
      iframeId = 'integrations-iframe-id',
      onEditorStateChange
    },
    ref
  ) => {
  const [isIFrameLoaded, setIsIFrameLoaded] = useState<boolean>(false);
  // True while a Node-RED editor is open full-page (header/tabs hidden, Back bar shown).
  const [isEditorMode, setIsEditorMode] = useState<boolean>(false);
  // True from the moment we navigate to a Node-RED editor until it finishes loading.
  // While true we show a full-screen blocking overlay so the user can't interact mid-transition.
  const [isEditorLoading, setIsEditorLoading] = useState<boolean>(false);
  const [iframeHeight, setIframeHeight] = useState<number>(
    getContentHeight(getWindowInnerHeight(), RESERVE_DEFAULT)
  );
  // The EasyIntegrate landing view by default. When EasyIntegrate asks us to open a
  // Node-RED editor, we swap this to the editor URL so it fills the whole integration page.
  const defaultSrc = `${EASY_INTEGRATE_BASE}${path}`;
  const [iframeSrc, setIframeSrc] = useState<string>(defaultSrc);

  // Set when we've navigated the iframe to a Node-RED editor but are still waiting for it
  // to finish loading. We only switch to full-page layout once the editor signals ready,
  // so the page doesn't expand before the editor is visible.
  const pendingEditorRef = useRef<boolean>(false);
  // Handshake targets that have actively handshaked with us. Stored as composite keys
  // `${origin}::${appType}` (appType = 'easyIntegrate' | 'nodeRed') so EasyIntegrate and a
  // Node-RED editor are tracked separately even when they share the same origin.
  const activeOriginsRef = useRef<Set<string>>(new Set<string>());
  // SECURITY_NOTE: Only origins in this allow-list are honoured by the message handler.
  const TRUSTED_ORIGINS = useRef<Set<string>>(
    new Set<string>([safeOrigin(EASY_INTEGRATE_BASE)].filter(Boolean))
  );

  const environmentVariables = (window as any).ENV;
  const AUTHORITY = environmentVariables?.AUTHORITY;
  const hstName = window.location.href;

  const getClientID = () => {
    const isMCDSSO =
      window.location.pathname.includes('mcd') ||
      getMCDEnabled(window.location.search) ||
      environmentVariables.MCD_INSTANCE ||
      ((window as any).ENV.ADMIN_MCD_HOSTNAME !== ''
        ? hstName.includes((window as any).ENV.ADMIN_MCD_HOSTNAME)
        : false);

    if (isMCDSSO) {
      return `${AUTHORITY}:${environmentVariables.AUTHORITY_CLIENT_ID_MCD}`;
    }
    if (localStorage.getItem(selectedLoginOption) === 'SSO') {
      return `${environmentVariables.AUTHORITY_HON_SSO}:${environmentVariables.AUTHORITY_CLIENT_ID_HON_SSO}`;
    }
    return `${AUTHORITY}:${environmentVariables.AUTHORITY_CLIENT_ID}`;
  };

  const getTheme = () => localStorage.getItem('COLOR_THEME') || '0';
  const getLocale = () => i18next.language || 'en-US';
  const getAccessToken = () =>
    JSON.parse(sessionStorage.getItem(`oidc.user:${getClientID()}`) || '{}')
      ?.access_token || '';
  const getIframeElement = () =>
    document.getElementById(iframeId) as HTMLIFrameElement;

  // FCIS project id is the currently selected customer/org id (persisted in sessionStorage
  // by the host page when the customer changes). Prefer the live org id, fall back to storage.
  const getFcisProjectId = () =>
    orgSelected?.id || sessionStorage.getItem('fcis_projectId') || '';

  // FCIS site id is the currently selected site (persisted in sessionStorage by the host
  // page when the site changes). Prefer the live org siteId, fall back to storage.
  const getFcisSiteId = () =>
    orgSelected?.siteId || sessionStorage.getItem('fcis_siteId') || '';

  const buildViewType = () => ({
    theme: getTheme(),
    locale: getLocale(),
    rbmAccessToken: getAccessToken(),
    fcisProjectId: getFcisProjectId(),
    fcisSiteId: getFcisSiteId(),
    selectedOrg: {
      id: orgSelected?.id || '056d1bda-c35e-438b-95a8-3666a18466c0',
      name: orgSelected?.name || 'c4474eaa-3b6e-47af-be2b-2699941fa4fe',
      siteId:
        orgSelected?.siteId ||
        sessionStorage.getItem('fcis_siteId') ||
        'c4474eaa-3b6e-47af-be2b-2699941fa4fe'
    }
  });

  // Tracks the last viewType payload we delivered to each origin so we never post the
  // SAME context twice. EasyIntegrate re-fetches its templates on every appContext
  // message (it fires both setTenantId and setSiteId), so duplicate/identical sends
  // hammer the API and trigger rate-limiting (429 Too Many Requests). By sending only
  // when the serialized context (org/site/token/theme/locale) actually changes, a page
  // load or a customer/site change results in exactly one appContext per origin.
  const lastSentByOrigin = useRef<Map<string, string>>(new Map());

  const postContextToOrigin = useCallback(
    (
      targetWindow: Window | null | undefined,
      origin: string,
      // Dedupe slot key. EasyIntegrate and a Node-RED editor can live on the SAME origin
      // (different paths/apps), and each must get its own context even when the serialized
      // payload is identical. So we dedupe per handshake target (origin + app type),
      // defaulting to the origin when a caller doesn't distinguish them.
      dedupeKey: string = origin
    ) => {
      if (!targetWindow || !origin) return;
      const viewType = JSON.stringify(buildViewType());
      if (lastSentByOrigin.current.get(dedupeKey) === viewType) {
        // Identical context already delivered to this target — skip to avoid 429s.
        return;
      }
      lastSentByOrigin.current.set(dedupeKey, viewType);
      console.log(
        '[EasyIntegrate] SENDING appContext to:',
        origin,
        `(target: ${dedupeKey})`,
        viewType
      );
      targetWindow.postMessage(
        { type: 'appContext', payload: { viewType } },
        origin
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgSelected, i18next.language]
  );

  // Enter editor mode: point the iframe at the editor URL but keep the normal layout
  // until the editor reports it has loaded (nodeRedEditorLoaded). This prevents the page
  // from going full-screen before the Node-RED editor is actually visible.
  const enterEditor = useCallback((url: string) => {
    setIframeSrc(url);
    pendingEditorRef.current = true;
    setIsEditorLoading(true);
  }, []);

  // Leave editor mode: restore the EasyIntegrate landing view and the page chrome.
  const exitEditor = useCallback(() => {
    pendingEditorRef.current = false;
    setIsEditorLoading(false);
    setIframeSrc(`${EASY_INTEGRATE_BASE}${path}`);
    setIsEditorMode(false);
    onEditorStateChange?.(false);
  }, [path, onEditorStateChange]);

  // Let the host page imperatively close the editor (used when the user presses browser back).
  useImperativeHandle(
    ref,
    () => ({
      closeEditor: () => exitEditor()
    }),
    [exitEditor]
  );
  // it to a Node-RED editor (pendingEditorRef), the editor is now visible, so this is the
  // right moment to expand to full-page. We rely on the iframe load event rather than a
  // postMessage handshake because the editor may not always send nodeRedEditorLoaded.
  const handleIframeLoad = useCallback(() => {
    setIsIFrameLoaded(true);
    if (pendingEditorRef.current) {
      pendingEditorRef.current = false;
      setIsEditorMode(true);
      setIsEditorLoading(false);
      onEditorStateChange?.(true);
    }
    // NOTE: we intentionally do NOT push context here. EasyIntegrate re-fetches its
    // templates on every appContext message, so we send the context exactly once when
    // it handshakes (and again only when the org/site/token actually changes) to avoid
    // hammering the API (429 Too Many Requests).
  }, [onEditorStateChange]);

  // Safety net: if this component unmounts (e.g. user switches tab) while an editor is
  // open, make sure the host page restores its header + tabs.
  useEffect(() => {
    return () => {
      onEditorStateChange?.(false);
    };
  }, [onEditorStateChange]);

  useLayoutEffect(() => {
    const handleResize = () =>
      setIframeHeight(
        getContentHeight(
          getWindowInnerHeight(),
          isEditorMode ? RESERVE_EDITOR : RESERVE_DEFAULT
        )
      );
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isEditorMode]);

  // Dynamic multi-origin listener:
  // EasyIntegrate signals via easyIntegrateLoaded: true
  // Dynamic URLs (Node-RED editors) signal via nodeRedEditorLoaded: true
  // Reply immediately to event.source + event.origin so token always goes to exact sender.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // SECURITY_NOTE: Reject any message from an origin outside our trust policy.
      if (!isTrustedOrigin(event.origin, TRUSTED_ORIGINS.current)) {
        console.log(
          '[EasyIntegrate] IGNORED message from untrusted origin:',
          event.origin,
          event.data
        );
        return;
      }

      const message = event.data;
      if (message?.type === 'appContext') {
        const raw = message?.payload?.viewType;
        const receivedVal =
          typeof raw === 'string' ? JSON.parse(raw || '{}') : raw || {};

        console.log(
          '[EasyIntegrate] RECEIVED appContext from:',
          event.origin,
          receivedVal
        );

        const isEasyIntegrateReady = receivedVal?.easyIntegrateLoaded === true;
        const isNodeRedEditorReady = receivedVal?.nodeRedEditorLoaded === true;

        if (isEasyIntegrateReady || isNodeRedEditorReady) {
          // Distinguish which app handshaked so EasyIntegrate and a Node-RED editor each get
          // their own context slot, even if they happen to share the same origin.
          const appType = isNodeRedEditorReady ? 'nodeRed' : 'easyIntegrate';
          const targetKey = `${event.origin}::${appType}`;
          // Remember this validated origin so future re-broadcasts reach it too.
          TRUSTED_ORIGINS.current.add(event.origin);
          // Register this handshake target (origin + app) as active.
          activeOriginsRef.current.add(targetKey);
          setIsIFrameLoaded(true);

          // Reply to the exact source that sent the signal. Deduped per target so we don't
          // re-post context it already has (prevents redundant template fetches / 429s).
          postContextToOrigin(event.source as Window, event.origin, targetKey);

          // The editor we navigated to has finished loading — NOW expand to full-page.
          if (isNodeRedEditorReady && pendingEditorRef.current) {
            pendingEditorRef.current = false;
            setIsEditorMode(true);
            setIsEditorLoading(false);
            onEditorStateChange?.(true);
          }
        }
      }

      // Navigation request from EasyIntegrate: open a Node-RED editor full-page in the
      // integration iframe. Contract:
      //   { type: 'openNodeRedEditor', payload: { url: '<node-red editor url>' } }
      if (message?.type === 'openNodeRedEditor') {
        const targetUrl: string = message?.payload?.url || '';
        const targetOrigin = safeOrigin(targetUrl);
        // SECURITY_NOTE: Only navigate to a URL whose origin passes our trust policy
        // (prevents an open-redirect / iframe-injection via a crafted message).
        if (
          targetUrl &&
          isTrustedOrigin(targetOrigin, TRUSTED_ORIGINS.current)
        ) {
          console.log(
            '[EasyIntegrate] RECEIVED openNodeRedEditor — navigating iframe to:',
            targetUrl
          );
          setIsIFrameLoaded(false);
          enterEditor(targetUrl);
        } else {
          console.log(
            '[EasyIntegrate] IGNORED openNodeRedEditor with untrusted/invalid url:',
            targetUrl
          );
        }
      }

      // Request to return to the EasyIntegrate landing view (close the editor).
      // Contract: { type: 'closeNodeRedEditor' }
      if (message?.type === 'closeNodeRedEditor') {
        console.log(
          '[EasyIntegrate] RECEIVED closeNodeRedEditor — returning to EasyIntegrate'
        );
        setIsIFrameLoaded(false);
        exitEditor();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSelected]);

  // Re-sends context to ALL origins that have been active (on org/site change or token
  // refresh). postContextToOrigin dedupes, so identical context is never re-sent.
  const sendContextToIframe = useCallback(() => {
    const iframeContentWindow = getIframeElement()?.contentWindow;
    if (!iframeContentWindow) return;
    // Post to every origin that has actively handshaked. If none have handshaked yet
    // (e.g. a customer/site selection happens before EasyIntegrate's first
    // `easyIntegrateLoaded`), fall back to the configured EasyIntegrate origin so the
    // selection still reaches EasyIntegrate. postContextToOrigin dedupes per origin.
    const targets =
      activeOriginsRef.current.size > 0
        ? Array.from(activeOriginsRef.current)
        : [`${safeOrigin(EASY_INTEGRATE_BASE)}::easyIntegrate`].filter(
            key => Boolean(key.split('::')[0])
          );
    targets.forEach(targetKey => {
      const origin = targetKey.split('::')[0];
      postContextToOrigin(iframeContentWindow, origin, targetKey);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postContextToOrigin]);

  useEffect(() => {
    if (isIFrameLoaded) {
      sendContextToIframe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orgSelected,
    isIFrameLoaded,
    i18next.language,
    localStorage.getItem('COLOR_THEME')
  ]);

  // Re-send context whenever RBM silently renews the token (automaticSilentRenew)
  useEffect(() => {
    if (!isIFrameLoaded) return;
    const handler = () => {
      sendContextToIframe();
    };
    authService.addUserLoaded(handler);
    return () => {
      authService.removeUserLoaded(handler);
    };
  }, [isIFrameLoaded, sendContextToIframe]);

  return (
    <div
      className={`easy-integrate-iframe-wrapper${
        isEditorMode ? ' editor-mode' : ''
      }`}
    >
      <iframe
        // SECURITY_NOTE: Re-mounting the iframe (via key) on every src change means each
        // load is the frame's *initial* navigation, which uses replacement semantics and
        // does NOT add an entry to the browser's shared session history. Without this,
        // switching src (landing -> editor) would push a spurious history entry, so the
        // first browser Back would only pop the iframe navigation (parent URL unchanged).
        key={iframeSrc}
        id={iframeId}
        title={path}
        src={iframeSrc}
        width="100%"
        height={iframeHeight}
        frameBorder="0"
        onLoad={handleIframeLoad}
        // SECURITY_NOTE: Constrain the embedded app capabilities via sandbox.
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
      />
      {isEditorLoading && (
        // Full-screen blocking overlay shown while the Node-RED editor loads so the
        // user can't interact with the page mid-transition.
        <div className="easy-integrate-loading-overlay">
          <Loader />
        </div>
      )}
    </div>
  );
  }
);

EasyIntegrateIframe.displayName = 'EasyIntegrateIframe';

export default EasyIntegrateIframe;